/**
 * Choosing the language, on the front page.
 *
 * The application already decides this well: `detectLocale` reads
 * `navigator.languages` in order and matches on the language subtag, so `pl-PL`,
 * `pl`, and a Polish-preferring browser in Germany all arrive in Polish. That is
 * the right signal — a person answered this question when they set their device
 * up, for their whole system rather than for us.
 *
 * Deliberately not country or IP. Somebody Polish in London gets English from a
 * country lookup and Polish from their browser, and the browser is the one they
 * actually chose. It also breaks for anybody travelling, on a VPN, or on a
 * carrier that routes through another country.
 *
 * What was missing was a way to override it, which matters most for the person
 * whose device is in a language they read less comfortably than the one they
 * think in. Two languages, so this is two buttons rather than a select: a menu
 * that only ever holds two things costs a click to discover what is in it.
 */

import { LOCALES, LOCALE_NAMES, useLocale, useT } from "../../lib/i18n.js";
import "./LocalePicker.css";

export function LocalePicker() {
  const t = useT();
  const { locale, setLocale } = useLocale();

  return (
    <div className="fc-locale" role="group" aria-label={t("Language")}>
      {LOCALES.map((code) => (
        <button
          key={code}
          type="button"
          lang={code}
          data-active={locale === code || undefined}
          aria-pressed={locale === code}
          onClick={() => setLocale(code)}
        >
          {/* The short form in the bar; the full name for anybody who cannot
              see which of two three-letter words is highlighted. */}
          <span aria-hidden="true">{code.toUpperCase()}</span>
          <span className="fc-sr-only">{LOCALE_NAMES[code]}</span>
        </button>
      ))}
    </div>
  );
}
